import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep, merge } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { ContentWithFooter } from '../../../../../../components/ui/content-with-footer';
import { ApplicationDefaultParameterSetFormikFormValues } from '../../../../shared/hook/application-default-parameter-set.hooks';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSetBaseFormProps,
  FlowItemForm,
} from '../../../../shared/model/flow.types';
import { BeaconPaintingData } from '../../model/beacon-painting-data';
import { PaintProcessSettingsForm } from '../flow-item/paint-process-settings-form';

export type PaintParameterSetBaseFormValidationBuilder = (
  dataSchema: yup.AnySchema<unknown, unknown, unknown> | undefined
) => yup.AnyObjectSchema;

interface PaintApplicationSetBaseFormTabbedProps<T>
  extends ApplicationDefaultParameterSetBaseFormProps<T> {
  onSubmit: (form: T, recommendationId?: string) => Promise<void>;
}

export type PaintApplicationSettingsFormValues = Pick<
  ApplicationDefaultParameterSetFormikFormValues,
  'tags' | 'data'
>;

function PaintApplicationSettingsBaseFormTabbed(
  props: PaintApplicationSetBaseFormTabbedProps<PaintApplicationSettingsFormValues>
): ReactElement {
  const {
    paramsReadOnly,
    system,
    footer,
    formValues,
    onSubmit,
    preForm,
    postForm,
    buildValidationSchema,
  } = props;

  const formikRef =
    useRef<FormikProps<PaintApplicationSettingsFormValues> | null>(null);

  const setInnerRef = useCallback(
    (instance: FormikProps<PaintApplicationSettingsFormValues> | null) => {
      formikRef.current = instance;
    },
    []
  );

  const [dataSchema, setDataSchema] = useState<yup.AnySchema>();

  const { flow, flowItemService } = useFlowServices();

  const validationSchema = useMemo(() => {
    return buildValidationSchema(dataSchema);
  }, [buildValidationSchema, dataSchema]);

  const setParameterValidationSchema = useCallback((schema?: yup.AnySchema) => {
    requestAnimationFrame(() => {
      setDataSchema(schema);
      formikRef.current?.validateForm();
    });
  }, []);

  const parameterOptions = merge(
    cloneDeep(BeaconPaintingData.DefaultPaintParameterMetadataFromFormOptions),
    {
      forceExcludeRefs: [
        ...(BeaconPaintingData.DefaultPaintParameterMetadataFromFormOptions
          .forceExcludeRefs ?? []),
      ],
    } as Partial<BeaconPaintingData.GetParameterMetadataFromFormOptions>
  );

  const recalculateParameterValues = useCallback(
    (form: FlowItemForm) => {
      const item = flowItemService.convertFormToFlowItem(form, system);

      const recalculatedItems = flowItemService.recalculateParameterValues(
        flow.config,
        [item],
        {
          isDefaultParameterSet: true,
        }
      );
      const recalculatedForm = flowItemService.convertFlowItemToForm(
        recalculatedItems[0],
        system
      );
      return recalculatedForm;
    },
    [flow.config, flowItemService, system]
  );

  const innerOnSubmit = (form: PaintApplicationSettingsFormValues) => {
    onSubmit(form);
  };

  return (
    <Formik
      initialValues={formValues}
      onSubmit={innerOnSubmit}
      enableReinitialize={true}
      validationSchema={validationSchema}
      innerRef={setInnerRef}
    >
      {(formikProps) => {
        const internalFooter = footer({
          formikProps,
          disabled: false,
        });
        const internalPreForm =
          typeof preForm === 'function' ? preForm({ formikProps }) : null;
        const internalPostForm =
          typeof postForm === 'function' ? postForm({ formikProps }) : null;

        return (
          <ContentWithFooter
            footer={internalFooter}
            forceHeightForFooter={true}
          >
            <Form>
              {internalPreForm}
              <div style={{ height: 20 }} />
              <PaintProcessSettingsForm
                system={system}
                readOnly={paramsReadOnly}
                formikValuePath="data"
                setValidationSchema={setParameterValidationSchema}
                parameterListPrefix="data.parameters"
                parameterOptions={parameterOptions}
                recalculateParameterValues={recalculateParameterValues}
              />
              {internalPostForm}
            </Form>
          </ContentWithFooter>
        );
      }}
    </Formik>
  );
}

export { PaintApplicationSettingsBaseFormTabbed };
